import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Search, MapPin, User, Briefcase, Phone, Mail, Globe, ArrowLeft, Home, Eye, LayoutDashboard, Star, X, Building2, Clock, Award, Facebook, Instagram, Linkedin, Twitter, Youtube } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useProviderAuth } from '@/context/ProviderAuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useIsNativeApp } from '../hooks/useIsNativeApp';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicSearch = () => {
  const navigate = useNavigate();
  const { agent } = useAuth();
  const { provider } = useProviderAuth();
  const isLoggedIn = !!agent || !!provider;
  const dashboardPath = provider ? '/provider/dashboard' : agent?.role === 'broker' ? '/broker' : '/dashboard';
  const [state, setState] = useState('NJ');
  const [query, setQuery] = useState('');
  const [searchType, setSearchType] = useState('agents');
  const [serviceType, setServiceType] = useState('');
  const [serviceTypes, setServiceTypes] = useState([]);
  const [results, setResults] = useState({ agents: [], providers: [] });
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [searched, setSearched] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [profileType, setProfileType] = useState(null); // 'agent' or 'provider'
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [bioExpanded, setBioExpanded] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const isNativeApp = useIsNativeApp();

  useEffect(() => {
    Promise.all([fetchStates(), fetchServiceTypes()]);
  }, []);

  // Autocomplete for both agents and providers
  useEffect(() => {
    if (query.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const params = { search_type: searchType, q: query.trim() };
        if (state) params.state = state;
        const res = await axios.get(`${API}/public/search`, { params });
        const items = searchType === 'agents'
          ? (res.data.agents || []).map(a => ({ ...a, _type: 'agent' }))
          : (res.data.providers || []).map(p => ({ ...p, _type: 'provider' }));
        setSuggestions(items.slice(0, 8));
        setShowSuggestions(items.length > 0);
      } catch {
        setSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, searchType, state]);

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API}/public/states`);
      setStates(response.data);
    } catch (error) {
      console.error('Failed to fetch states:', error);
    }
  };

  const fetchServiceTypes = async () => {
    try {
      const response = await axios.get(`${API}/public/service-types`);
      setServiceTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch service types:', error);
    }
  };

  const handleSearch = async (pageOverride) => {
    if (!state && !query) {
      toast.error('Please select a state or enter a search term');
      return;
    }

    setLoading(true);
    setSearched(true);

    try {
      const params = {
        search_type: searchType,
        page: pageOverride || page,
        per_page: perPage,
      };
      if (state) params.state = state;
      if (query) params.q = query;
      if (serviceType && serviceType !== 'all_types' && searchType !== 'agents') {
        params.service_type = serviceType;
      }
      const response = await axios.get(`${API}/public/search`, { params });
      setResults(response.data);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch when page or perPage changes (only after initial search)
  useEffect(() => {
    if (searched) handleSearch();
  }, [page, perPage]);

  const formatPhone = (phone) => {
    if (!phone) return 'N/A';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const openProfile = async (id, type) => {
    setProfileType(type);
    setSelectedProfile(id);
    // Use search result data first (has bio, specializations, languages, etc.)
    const cardData = type === 'agent'
      ? results.agents.find(a => a.id === id)
      : results.providers.find(p => p.id === id);
    setProfileData(cardData || null);
    // Enrich with API data (has upcoming open houses, reviews, etc.)
    try {
      const endpoint = type === 'agent' ? `/public/agent/${id}` : `/public/provider/${id}`;
      const res = await axios.get(`${API}${endpoint}`);
      setProfileData(prev => ({ ...prev, ...res.data, ...(cardData || {}) }));
    } catch {}
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100">
    
      {/* Header */}
    {!isNativeApp && (
      <div className="bg-white border-b border-stone-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 md:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                size="sm"
                className="text-stone-600 hover:text-[#1A4D2E]"
              >
                <ArrowLeft size={18} className="mr-2" />
                Back to Home
              </Button>
              {isLoggedIn && (
                <Button
                  onClick={() => navigate(dashboardPath)}
                  variant="outline"
                  size="sm"
                  className="border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white"
                  data-testid="back-to-dashboard"
                >
                  <LayoutDashboard size={16} className="mr-2" />
                  Dashboard
                </Button>
              )}
            </div>
            <Home
              className="text-[#1A4D2E] cursor-pointer hover:text-[#15401F]"
              size={32}
              onClick={() => navigate('/')}
            />
          </div>
          <h1 className="text-sm sm:text-2xl md:text-3xl font-bold text-[#1A4D2E] mt-3" style={{fontFamily: '"Playfair Display", serif'}}>
            Find Real Estate Professionals & Service Providers
          </h1>
        </div>
      </div>
  )}

      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        {/* Search Box */}
        <Card className="p-6 mb-6 bg-white shadow-lg" data-testid="search-form-card">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {/* Search Input */}
            <div className="md:col-span-5">
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Search
              </label>
              <div className="relative">
                <Input
                  data-testid="search-query-input"
                  type="text"
                  placeholder={searchType === 'providers' ? "Type provider name, company, or service..." : "Search by name, company, email, or location..."}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { setShowSuggestions(false); setPage(1); handleSearch(1); } }}
                  onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  className="w-full"
                  autoComplete="off"
                />
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-lg shadow-lg max-h-72 overflow-y-auto" data-testid="autocomplete-dropdown">
                    {suggestions.map((p) => {
                      const isAgent = p._type === 'agent';
                      const displayName = p.name || p.full_name || p.company_name || '?';
                      const subtitle = isAgent
                        ? [p.role && p.role.charAt(0).toUpperCase() + p.role.slice(1), p.city && `in ${p.city}`].filter(Boolean).join(' ')
                        : [p.service_type || p.service_types?.join(', '), p.city && `in ${p.city}`].filter(Boolean).join(' ');
                      return (
                      <button
                        key={p.id}
                        className="w-full text-left px-4 py-2.5 hover:bg-stone-50 flex items-center gap-3 border-b border-stone-50 last:border-0 transition-colors"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setSuggestions([]);
                          setShowSuggestions(false);
                          openProfile(p.id, isAgent ? 'agent' : 'provider');
                        }}
                        data-testid={`suggestion-${p.id}`}
                      >
                        {p.photo_url ? (
                          <img src={p.photo_url} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                        ) : (
                        <div className={`w-8 h-8 rounded-full ${isAgent ? 'bg-[#1A4D2E]' : 'bg-purple-600'} flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>
                          {displayName.charAt(0)}
                        </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-stone-900 truncate">{displayName}</p>
                          {subtitle && <p className="text-xs text-stone-500 truncate">{subtitle}</p>}
                        </div>
                      </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Looking For */}
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Looking For
              </label>
              <Select value={searchType} onValueChange={(val) => { setSearchType(val); if (val === 'agents') setServiceType(''); setResults({ agents: [], providers: [] }); setSearched(false); setTotal(0); setTotalPages(1); setPage(1); }} data-testid="search-type-select">
                <SelectTrigger data-testid="search-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="agents">Agents & Brokers</SelectItem>
                  <SelectItem value="providers">Service Providers</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* State Dropdown */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-stone-700 mb-2">
                State <span className="text-red-500">*</span>
              </label>
              <Select value={state} onValueChange={(val) => setState(val)} data-testid="state-select">
                <SelectTrigger data-testid="state-select">
                  <SelectValue placeholder="Select State" />
                </SelectTrigger>
                <SelectContent>
                  {states.map((s) => (
                    <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                  ))}
                  {states.length === 0 && <SelectItem value="NJ">New Jersey</SelectItem>}
                </SelectContent>
              </Select>
            </div>

            {/* Search Button */}
            <div className="md:col-span-2 flex items-end">
              <Button
                data-testid="search-button"
                onClick={() => { setShowSuggestions(false); setPage(1); handleSearch(1); }}
                disabled={loading}
                className="w-full bg-[#1A4D2E] hover:bg-[#15401F] text-white"
              >
                <Search size={18} className="mr-2" />
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </div>
          </div>
        </Card>

        {/* Results */}
        {searched && !loading && (
          <>
            {total > 0 && (
              <p className="text-sm text-stone-600 mb-4" data-testid="search-results-count">
                {total} result{total !== 1 ? 's' : ''} found
                {state && ` in ${state}`}
                {query && ` matching "${query}"`}
              </p>
            )}

            {/* Pagination Top */}
            {total > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pb-4 mb-4 border-b border-stone-200" data-testid="search-pagination-top">
                <div className="flex items-center gap-2 text-sm text-stone-600">
                  <span>Show</span>
                  <select
                    value={perPage}
                    onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                    className="border border-stone-300 rounded px-2 py-1 text-sm bg-white"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                  <span>per page</span>
                  <span className="text-stone-400 ml-2">|</span>
                  <span className="ml-2">{(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(1)}>First</Button>
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                  <span className="px-3 py-1 text-sm font-medium text-stone-700">Page {page} of {totalPages}</span>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
                  <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>Last</Button>
                </div>
              </div>
            )}

            {/* Agents Section */}
            {(searchType === 'all' || searchType === 'agents') && results.agents.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-stone-900 mb-4 flex items-center gap-2">
                  <User size={24} className="text-[#1A4D2E]" />
                  Agents & Brokers ({results.agents.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {results.agents.map((agent) => (
                    <Card key={agent.id} className="p-5 hover:shadow-lg transition-shadow">
                      <div className="flex items-start gap-4">
                        {agent.photo_url ? (
                          <img src={agent.photo_url} alt="" className="w-16 h-16 rounded-full object-cover flex-shrink-0" />
                        ) : (
                        <div className="w-16 h-16 rounded-full bg-[#1A4D2E] flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                          {agent.name.charAt(0)}
                        </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-lg text-stone-900 truncate">{agent.name}</h3>
                          <p className="text-sm text-stone-600 capitalize">{agent.role}</p>
                          {agent.title && <p className="text-sm text-stone-500 italic">{agent.title}</p>}
                          {agent.average_rating > 0 && (
                            <div className="flex items-center gap-0.5 mt-1">
                              <Star size={13} className="text-amber-500 fill-amber-500" />
                              <span className="text-sm font-semibold text-stone-800">{agent.average_rating.toFixed(1)}</span>
                              <span className="text-xs text-stone-500">({agent.total_reviews})</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-4 space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-stone-700">
                          <MapPin size={16} className="text-[#1A4D2E] flex-shrink-0" />
                          <span className="truncate">{agent.city}, {agent.state} {agent.zip_code}</span>
                        </div>
                        {agent.email && (
                          <div className="flex items-center gap-2 text-stone-700">
                            <Mail size={16} className="text-[#1A4D2E] flex-shrink-0" />
                            <span className="truncate">{agent.email}</span>
                          </div>
                        )}
                        {agent.phone && (
                          <div className="flex items-center gap-2 text-stone-700">
                            <Phone size={16} className="text-[#1A4D2E] flex-shrink-0" />
                            <span>{formatPhone(agent.phone)}</span>
                          </div>
                        )}
                        {agent.years_of_experience && (
                          <p className="text-stone-600">Experience: {agent.years_of_experience} years</p>
                        )}
                      </div>

                      {agent.bio && (
                        <p className="mt-3 text-sm text-stone-600 line-clamp-2">{agent.bio}</p>
                      )}

                      <Button
                        data-testid={`view-agent-${agent.id}`}
                        onClick={() => openProfile(agent.id, 'agent')}
                        variant="outline"
                        size="sm"
                        className="w-full mt-4 border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white"
                      >
                        <Eye size={16} className="mr-2" />
                        View Profile
                      </Button>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Service Providers Section */}
            {(searchType === 'all' || searchType === 'providers') && results.providers.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-stone-900 mb-4 flex items-center gap-2">
                  <Briefcase size={24} className="text-[#1A4D2E]" />
                  Service Providers ({results.providers.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {results.providers.map((provider) => (
                    <Card key={provider.id} className="p-5 hover:shadow-lg transition-shadow">
                      <div className="flex items-start gap-4">
                        {provider.photo_url ? (
                          <img src={provider.photo_url} alt="" className="w-16 h-16 rounded-full object-cover flex-shrink-0" />
                        ) : (
                        <div className="w-16 h-16 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
                          {provider.name.charAt(0)}
                        </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-lg text-stone-900 truncate">{provider.name}</h3>
                          {provider.company_name && (
                            <p className="text-sm text-stone-600">{provider.company_name}</p>
                          )}
                          {provider.service_type && (
                            <p className="text-sm text-purple-600 font-medium">{provider.service_type}</p>
                          )}
                          {provider.average_rating > 0 && (
                            <div className="flex items-center gap-0.5 mt-1">
                              <Star size={13} className="text-amber-500 fill-amber-500" />
                              <span className="text-sm font-semibold text-stone-800">{provider.average_rating.toFixed(1)}</span>
                              <span className="text-xs text-stone-500">({provider.total_reviews})</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-4 space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-stone-700">
                          <MapPin size={16} className="text-purple-600 flex-shrink-0" />
                          <span className="truncate">{provider.city}, {provider.state} {provider.zip_code}</span>
                        </div>
                        {provider.email && (
                          <div className="flex items-center gap-2 text-stone-700">
                            <Mail size={16} className="text-purple-600 flex-shrink-0" />
                            <span className="truncate">{provider.email}</span>
                          </div>
                        )}
                        {provider.phone && (
                          <div className="flex items-center gap-2 text-stone-700">
                            <Phone size={16} className="text-purple-600 flex-shrink-0" />
                            <span>{formatPhone(provider.phone)}</span>
                          </div>
                        )}
                        {provider.hourly_rate && (
                          <p className="text-stone-600">Rate: {provider.hourly_rate}</p>
                        )}
                      </div>

                      {provider.bio && (
                        <p className="mt-3 text-sm text-stone-600 line-clamp-2">{provider.bio}</p>
                      )}

                      <Button
                        data-testid={`view-provider-${provider.id}`}
                        onClick={() => openProfile(provider.id, 'provider')}
                        variant="outline"
                        size="sm"
                        className="w-full mt-4 border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white"
                      >
                        <Eye size={16} className="mr-2" />
                        View Profile
                      </Button>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* No Results */}
            {results.agents.length === 0 && results.providers.length === 0 && (
              <Card className="p-12 text-center">
                <Search size={48} className="mx-auto text-stone-400 mb-4" />
                <h3 className="text-xl font-semibold text-stone-900 mb-2">No Results Found</h3>
                <p className="text-stone-600">
                  No professionals found{query ? ` matching "${query}"` : ''} in {state}. Try a different search.
                </p>
              </Card>
            )}
          </>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A4D2E]"></div>
          </div>
        )}

        {/* Pagination */}
        {searched && !loading && total > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-stone-200 mt-4" data-testid="search-pagination">
            <div className="flex items-center gap-2 text-sm text-stone-600">
              <span>Show</span>
              <select
                value={perPage}
                onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                className="border border-stone-300 rounded px-2 py-1 text-sm bg-white"
                data-testid="search-per-page-select"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <span>per page</span>
              <span className="text-stone-400 ml-2">|</span>
              <span className="ml-2">{(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total}</span>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(1)} data-testid="search-page-first">First</Button>
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="search-page-prev">Prev</Button>
              <span className="px-3 py-1 text-sm font-medium text-stone-700">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="search-page-next">Next</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(totalPages)} data-testid="search-page-last">Last</Button>
            </div>
          </div>
        )}

        {/* Profile Popup */}
        <Dialog open={!!selectedProfile} onOpenChange={(open) => { if (!open) { setSelectedProfile(null); setProfileData(null); setBioExpanded(false); } }}>
          <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto p-0 gap-0" data-testid="profile-popup" aria-describedby="profile-popup-desc">
            {profileLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1A4D2E]"></div>
              </div>
            ) : profileData && (() => {
              const isAgent = profileType === 'agent';
              const accentBg = isAgent ? 'bg-[#1A4D2E]' : 'bg-purple-600';
              const accentText = isAgent ? 'text-[#1A4D2E]' : 'text-purple-600';
              const accentBorder = isAgent ? 'border-[#1A4D2E]' : 'border-purple-600';
              const accentLightBg = isAgent ? 'bg-[#1A4D2E]/8' : 'bg-purple-50';
              const formatCity = (c) => c ? c.replace(/\b\w/g, l => l.toUpperCase()) : '';
              const uniqueServices = [...new Set((profileData.service_types || []).map(s => s.trim()))];
              const reviews = profileData.reviews || [];
              const topReviews = reviews.filter(r => r.rating >= 4).sort((a, b) => b.rating - a.rating).slice(0, 3);
              const avgRating = profileData.profile?.average_rating || profileData.average_rating || 0;
              const totalReviews = profileData.profile?.total_reviews || profileData.total_reviews || 0;
              const socialMedia = profileData.profile?.social_media || profileData.social_media || {};
              const hasSocials = Object.values(socialMedia).some(v => v);
              const agencyName = profileData.profile?.agency_name;

              return (
                <>
                  <DialogHeader className="sr-only">
                    <DialogTitle>Profile</DialogTitle>
                    <p id="profile-popup-desc">Professional profile details</p>
                  </DialogHeader>

                  {/* Header Banner */}
                  <div className="px-6 pt-6 pb-4">
                    <div className="flex items-start gap-4">
                      {profileData.photo_url ? (
                        <img src={profileData.photo_url} alt="" className={`w-16 h-16 rounded-full object-cover flex-shrink-0`} />
                      ) : (
                      <div className={`w-16 h-16 rounded-full ${accentBg} flex items-center justify-center text-white font-bold text-xl flex-shrink-0`}>
                        {(profileData.name || profileData.full_name || '?').charAt(0)}
                      </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h2 className="font-semibold text-lg text-stone-900" data-testid="profile-popup-name">
                          {profileData.name || profileData.full_name}
                        </h2>
                        {isAgent && profileData.role && (
                          <p className="text-sm text-stone-600 capitalize">{profileData.role}</p>
                        )}
                        {isAgent && (profileData.title || profileData.public_profile?.title) && (
                          <p className="text-sm text-stone-500 italic">{profileData.title || profileData.public_profile?.title}</p>
                        )}
                        {!isAgent && profileData.company_name && (
                          <p className="text-sm text-stone-600">{profileData.company_name}</p>
                        )}
                        {!isAgent && profileData.service_type && (
                          <p className={`text-sm ${accentText} font-medium`}>{profileData.service_type}</p>
                        )}
                        {agencyName && (
                          <p className="text-sm text-stone-500 flex items-center gap-1 mt-0.5">
                            <Building2 size={13} /> {agencyName}
                          </p>
                        )}
                        {/* Rating badge */}
                        {avgRating > 0 && (
                          <div className="flex items-center gap-1.5 mt-2">
                            <div className="flex items-center gap-0.5 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
                              <Star size={13} className="text-amber-500 fill-amber-500" />
                              <span className="text-sm font-semibold text-stone-800">{avgRating.toFixed(1)}</span>
                              <span className="text-xs text-stone-500">({totalReviews})</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Body Content */}
                  <div className="px-6 py-4 space-y-4">
                    {/* Contact Info Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm" data-testid="profile-popup-contact">
                      {(profileData.city || profileData.state) && (
                        <div className="flex items-center gap-2 text-stone-700">
                          <MapPin size={15} className={`${accentText} flex-shrink-0`} />
                          <span className="truncate">{[formatCity(profileData.city), profileData.state, profileData.zip_code].filter(Boolean).join(', ')}</span>
                        </div>
                      )}
                      {profileData.email && (
                        <div className="flex items-center gap-2 text-stone-700">
                          <Mail size={15} className={`${accentText} flex-shrink-0`} />
                          <a href={`mailto:${profileData.email}`} className="hover:underline truncate">{profileData.email}</a>
                        </div>
                      )}
                      {profileData.phone && (
                        <div className="flex items-center gap-2 text-stone-700">
                          <Phone size={15} className={`${accentText} flex-shrink-0`} />
                          <a href={`tel:${profileData.phone}`}>{formatPhone(profileData.phone)}</a>
                        </div>
                      )}
                      {profileData.years_of_experience && (
                        <div className="flex items-center gap-2 text-stone-700">
                          <Award size={15} className={`${accentText} flex-shrink-0`} />
                          <span>{profileData.years_of_experience} years experience</span>
                        </div>
                      )}
                      {(profileData.website || profileData.profile?.website) && (
                        <div className="flex items-center gap-2 text-stone-700">
                          <Globe size={15} className={`${accentText} flex-shrink-0`} />
                          {(() => {
                            const w = profileData.website || profileData.profile?.website;
                            return <a href={w.startsWith('http') ? w : `https://${w}`} target="_blank" rel="noopener noreferrer" className="hover:underline truncate">{w}</a>;
                          })()}
                        </div>
                      )}
                      {profileData.license_id && (
                        <div className="flex items-center gap-2 text-stone-700">
                          <Briefcase size={15} className={`${accentText} flex-shrink-0`} />
                          <span>License: {profileData.license_id}</span>
                        </div>
                      )}
                      {!isAgent && profileData.hourly_rate && (
                        <div className="flex items-center gap-2 text-stone-700">
                          <Clock size={15} className={`${accentText} flex-shrink-0`} />
                          <span>Rate: {profileData.hourly_rate}</span>
                        </div>
                      )}
                    </div>

                    {/* Social Media */}
                    {hasSocials && (
                      <div className="flex items-center gap-3 pt-1">
                        {socialMedia.facebook && (
                          <a href={`https://facebook.com/${socialMedia.facebook}`} target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-blue-600 transition-colors" data-testid="social-facebook">
                            <Facebook size={18} />
                          </a>
                        )}
                        {socialMedia.instagram && (
                          <a href={`https://instagram.com/${socialMedia.instagram}`} target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-pink-600 transition-colors" data-testid="social-instagram">
                            <Instagram size={18} />
                          </a>
                        )}
                        {socialMedia.linkedin && (
                          <a href={`https://linkedin.com/in/${socialMedia.linkedin}`} target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-blue-700 transition-colors" data-testid="social-linkedin">
                            <Linkedin size={18} />
                          </a>
                        )}
                        {socialMedia.twitter && (
                          <a href={`https://twitter.com/${socialMedia.twitter}`} target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-sky-500 transition-colors" data-testid="social-twitter">
                            <Twitter size={18} />
                          </a>
                        )}
                        {socialMedia.youtube && (
                          <a href={`https://youtube.com/${socialMedia.youtube}`} target="_blank" rel="noopener noreferrer" className="text-stone-400 hover:text-red-600 transition-colors" data-testid="social-youtube">
                            <Youtube size={18} />
                          </a>
                        )}
                      </div>
                    )}

                    {/* Bio */}
                    {(profileData.bio || profileData.about) && (
                      <div className="pt-3 border-t border-stone-100">
                        <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-1.5">About</h3>
                        <p className={`text-sm text-stone-700 leading-relaxed whitespace-pre-line ${!bioExpanded ? 'line-clamp-3' : ''}`}>{profileData.bio || profileData.about}</p>
                        {!bioExpanded && (
                          <button onClick={() => setBioExpanded(true)} className={`text-sm font-medium ${accentText} hover:underline mt-1`} data-testid="bio-more-link">more</button>
                        )}
                      </div>
                    )}

                    {/* Specializations */}
                    {profileData.specializations?.length > 0 && (
                      <div className="pt-3 border-t border-stone-100">
                        <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2">Specializations</h3>
                        <div className="flex flex-wrap gap-1.5">
                          {profileData.specializations.map((s) => (
                            <span key={s} className={`px-2.5 py-1 ${accentLightBg} ${accentText} text-xs font-medium rounded-full`}>{s}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Languages */}
                    {profileData.languages?.length > 0 && (
                      <div className="pt-3 border-t border-stone-100">
                        <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2">Languages</h3>
                        <div className="flex flex-wrap gap-1.5">
                          {profileData.languages.map((l) => (
                            <span key={l} className="px-2.5 py-1 bg-blue-50 text-blue-700 text-xs font-medium rounded-full">{l}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Service Areas - formatted cities */}
                    {profileData.service_areas?.length > 0 && (
                      <div className="pt-3 border-t border-stone-100">
                        <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2">Service Areas</h3>
                        <div className="flex flex-wrap gap-1.5">
                          {profileData.service_areas.map((area, idx) => (
                            <span key={idx} className="px-2.5 py-1 bg-stone-100 text-stone-700 text-xs font-medium rounded-full">{formatCity(area.city)}, {area.state}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Upcoming Open Houses */}
                    {profileData.upcoming_open_houses?.length > 0 && (
                      <div className="pt-3 border-t border-stone-100">
                        <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2">Upcoming Open Houses</h3>
                        <div className="space-y-2">
                          {profileData.upcoming_open_houses.map((oh) => (
                            <div key={oh.id} className="p-2.5 bg-stone-50 rounded-lg text-sm flex items-start gap-2">
                              <Home size={14} className={`${accentText} mt-0.5 flex-shrink-0`} />
                              <div>
                                <p className="font-medium text-stone-800">{oh.property_address}</p>
                                <p className="text-stone-500 text-xs mt-0.5">{new Date(oh.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} at {oh.time}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Top Reviews */}
                    {topReviews.length > 0 && (
                      <div className="pt-3 border-t border-stone-100" data-testid="profile-popup-reviews">
                        <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2">Top Reviews</h3>
                        <div className="space-y-2.5">
                          {topReviews.map((review, idx) => (
                            <div key={idx} className="p-3 bg-amber-50/60 rounded-lg border border-amber-100">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center gap-1">
                                  {[...Array(5)].map((_, i) => (
                                    <Star key={i} size={12} className={i < review.rating ? 'text-amber-500 fill-amber-500' : 'text-stone-300'} />
                                  ))}
                                </div>
                                {review.reviewer_name && (
                                  <span className="text-xs font-medium text-stone-500">{review.reviewer_name}</span>
                                )}
                              </div>
                              {(review.review_text || review.comment) && (
                                <p className="text-sm text-stone-700 line-clamp-3">{review.review_text || review.comment}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="pt-4 border-t border-stone-100 flex justify-end pb-2">
                      <Button
                        data-testid="profile-popup-close"
                        onClick={() => { setSelectedProfile(null); setProfileData(null); setBioExpanded(false); }}
                        variant="outline"
                        size="sm"
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>

      </div>
    </div>
  );
};

export default PublicSearch;
